import React, { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

import { PublicPage } from 'api/website';
import { getIsContentFullWidthLayout } from 'cms/helpers';
import { MappedContentBlock } from 'cms/mappers';
import { Box } from 'components/Box';
import Grid, { GridItem } from 'components/Grid';
import HideContainer from 'containers/HideContainer';
import { gutterWidth } from 'theme';

import { ContentPageBlocksWithContainer } from './ContentPageBlocksWithContainer';
import { ContentPageGridSidebar } from './ContentPageGridSidebar';

const Content = styled.div`
  flex: 1 1 auto;
  width: 100%;
`;

type Props = {
  content: PublicPage;
  mappedBlocks: MappedContentBlock[];
  upperSidebar: ReactNode;
  lowerSidebar: ReactNode;
  footer: ReactElement | undefined;
};

export function ContentPageBodyMainContent({
  content,
  mappedBlocks,
  upperSidebar,
  lowerSidebar,
  footer,
}: Props) {
  const { layout } = content;
  const fullWidthLayout = getIsContentFullWidthLayout({ content });
  const hasSidebar = Boolean(upperSidebar || lowerSidebar);

  if (hasSidebar) {
    return (
      <Grid>
        <HideContainer lg xl noWrapper>
          {upperSidebar && (
            <ContentPageGridSidebar sidebar={upperSidebar} position="upper" />
          )}
        </HideContainer>

        <GridItem columnStart={[1, 1, 1, 1]} columnEnd={[13, 13, 8, 9]}>
          <Content
            data-qa-id="cms-main-content"
            data-qa-content-layout={layout}
          >
            <ContentPageBlocksWithContainer
              mappedBlocks={mappedBlocks}
              fullWidthLayout={fullWidthLayout}
              withSidebar
            />
          </Content>
          {footer && <Box mt={gutterWidth}>{footer}</Box>}
        </GridItem>

        <HideContainer xs sm md noWrapper>
          {(upperSidebar || lowerSidebar) && (
            <ContentPageGridSidebar
              sidebar={
                <>
                  {upperSidebar}
                  <Box mt={upperSidebar && lowerSidebar ? '40px' : undefined}>
                    {lowerSidebar}
                  </Box>
                </>
              }
              position="upper"
            />
          )}
        </HideContainer>

        <HideContainer lg xl noWrapper>
          {lowerSidebar && (
            <ContentPageGridSidebar sidebar={lowerSidebar} position="lower" />
          )}
        </HideContainer>
      </Grid>
    );
  }

  return (
    <Content data-qa-id="cms-main-content" data-qa-content-layout={layout}>
      <ContentPageBlocksWithContainer
        mappedBlocks={mappedBlocks}
        fullWidthLayout={fullWidthLayout}
      />

      {footer && (
        <Grid>
          <GridItem
            columnStart={[1, 1, 1, fullWidthLayout ? 1 : 2]}
            columnEnd={[13, 13, 13, fullWidthLayout ? 13 : 12]}
          >
            <Box mt={gutterWidth}>{footer}</Box>
          </GridItem>
        </Grid>
      )}
    </Content>
  );
}
